import React, { useEffect, useState } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";

const NAV_LINKS = [
  { to: "/websites", label: "Websites" },
  { to: "/revenue-systems", label: "Revenue Systems" },
  { to: "/pricing", label: "Pricing" },
  { to: "/packages", label: "Packages" },
  { to: "/faq", label: "FAQ" },
];

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const handleBook = () => {
    setOpen(false);
    window.dispatchEvent(new Event("open-audit"));
  };

  return (
    <header
      data-testid="nav-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-sand-100/95 backdrop-blur-md border-b border-sand-300 shadow-[0_4px_20px_-12px_rgba(0,0,0,0.12)]"
          : "bg-sand-100/85 backdrop-blur-md border-b border-sand-300/70"
      }`}
    >
      <nav className="max-w-7xl mx-auto px-5 sm:px-6 md:px-10 h-16 md:h-20 flex items-center justify-between gap-2">
        <Link
          to="/"
          className="group flex items-center gap-2 sm:gap-3 min-w-0"
          data-testid="nav-wordmark"
        >
          <span className="font-display text-[15px] sm:text-[17px] md:text-lg font-semibold tracking-tight text-ink truncate">
            The Modern Exit
          </span>
          <span className="hidden xl:inline-block h-3 w-px bg-sand-300" />
          <span className="hidden xl:inline-block eyebrow !text-[10px] whitespace-nowrap">
            Funnels to Freedom
          </span>
        </Link>

        <ul className="hidden lg:flex items-center gap-7 xl:gap-9">
          {NAV_LINKS.map((l) => (
            <li key={l.to}>
              <NavLink
                to={l.to}
                data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `text-sm transition-colors ${
                    isActive
                      ? "text-ink font-medium"
                      : "text-ink/75 hover:text-ink"
                  }`
                }
              >
                {l.label}
              </NavLink>
            </li>
          ))}
        </ul>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={handleBook}
            data-testid="nav-book-audit-btn"
            data-magnetic
            className="hidden md:inline-flex items-center justify-center rounded-full bg-ink-deep text-sand-100 px-5 py-2.5 text-sm font-medium hover:bg-ink transition-colors"
          >
            Book Audit
          </button>
          <button
            type="button"
            aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open}
            className="lg:hidden inline-flex items-center justify-center w-11 h-11 rounded-full border border-sand-300 bg-sand-50/80 text-ink"
            onClick={() => setOpen((s) => !s)}
            data-testid="nav-mobile-toggle"
          >
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </nav>

      {/* Mobile menu */}
      <div
        className={`lg:hidden overflow-hidden transition-[max-height] duration-300 ${
          open ? "max-h-[460px]" : "max-h-0"
        } bg-sand-100/95 backdrop-blur border-t border-sand-300`}
        data-testid="nav-mobile-menu"
      >
        <ul className="px-5 sm:px-6 py-4 space-y-1">
          {NAV_LINKS.map((l) => (
            <li key={l.to}>
              <NavLink
                to={l.to}
                onClick={() => setOpen(false)}
                className="block py-3 text-base text-ink"
                data-testid={`nav-mobile-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {l.label}
              </NavLink>
            </li>
          ))}
          <li className="pt-2 pb-3">
            <button
              onClick={handleBook}
              className="w-full inline-flex items-center justify-center rounded-full bg-ink-deep text-sand-100 px-5 py-3.5 text-[15px] font-medium min-h-[52px]"
              data-testid="nav-mobile-book-audit-btn"
            >
              Book Audit
            </button>
          </li>
        </ul>
      </div>
    </header>
  );
}
